import React from 'react';
import { ReportTheme } from '../../types';

interface TopReferralsTableWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const TopReferralsTableWidget: React.FC<TopReferralsTableWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('TopReferralsTableWidget raw data:', data);

  // Process the raw API data internally for Top Referrals
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('TopReferralsTableWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const dateEntries = Object.entries(rawData);
    
    if (dateEntries.length === 0) {
      console.warn('TopReferralsTableWidget: No data entries found');
      return null;
    }

    // Aggregate referral data across all dates
    const referralTotals: { [key: string]: { sessions: number; pageViews: number; bounceRate: number; bounceRateCount: number } } = {};

    dateEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each referral in the day's data
      Object.entries(dayData).forEach(([referralKey, referralData]: [string, any]) => {
        if (referralData && typeof referralData === 'object') {
          const sessions = referralData.sessions || referralData.count || 0;
          const pageViews = referralData.pageviews || 0;
          const bounceRate = referralData.bounce_rate || 0;

          if (!referralTotals[referralKey]) {
            referralTotals[referralKey] = { sessions: 0, pageViews: 0, bounceRate: 0, bounceRateCount: 0 };
          }

          referralTotals[referralKey].sessions += sessions;
          referralTotals[referralKey].pageViews += pageViews;
          referralTotals[referralKey].bounceRate += bounceRate * sessions; // Weighted average
          referralTotals[referralKey].bounceRateCount += sessions;
        }
      });
    });

    if (Object.keys(referralTotals).length === 0) {
      console.warn('TopReferralsTableWidget: No referral data found');
      return null;
    }
    
    const tableData = Object.entries(referralTotals)
      .map(([referralKey, data]) => ({
        referral: referralKey,
        sessions: data.sessions,
        pageViews: data.pageViews,
        bounceRate: data.bounceRateCount > 0 
          ? ((data.bounceRate / data.bounceRateCount) * 100).toFixed(1) + '%'
          : '0.0%'
      }))
      .sort((a, b) => b.sessions - a.sessions) // Sort by sessions descending
      .slice(0, 10); // Take top 10 referrals

    return tableData.length > 0 ? tableData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <div className="w-full h-full overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left p-2 font-medium">Referral Source</th>
            <th className="text-right p-2 font-medium">Sessions</th>
            <th className="text-right p-2 font-medium">Page Views</th>
            <th className="text-right p-2 font-medium">Bounce Rate</th>
          </tr>
        </thead>
        <tbody>
          {processedData.map((row, index) => (
            <tr key={index} className="border-b hover:bg-muted/50">
              <td className="p-2">
                <div className="truncate max-w-[200px]" title={row.referral}>
                  {row.referral}
                </div>
              </td>
              <td className="text-right p-2">{row.sessions.toLocaleString()}</td>
              <td className="text-right p-2">{row.pageViews.toLocaleString()}</td>
              <td className="text-right p-2">{row.bounceRate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


